import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Link,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import { useLazyQuery, useMutation, useApolloClient } from "@apollo/client";
import { ACCOUNT_DELETION_IMPACT } from "../graphql/query";
import { DELETE_MY_ACCOUNT } from "../graphql/mutation";
import { useSession } from "../context/SessionContext";
import CustomSnackbar from "./Snackbar";

// Student-facing "Delete Account" flow. Client decision #6/#8: a self-delete does NOT
// hard-delete — it CLOSES (archives) the account; personal data is retained per the
// data-protection policy and purged at the retention window. Any remaining booked
// classes are cancelled with no refund, and the student must type "Delete" to confirm.
const SUPPORT_EMAIL =
  (process.env.REACT_APP_SUPPORT_EMAIL as string) || "theunitedgrooves@gmail.com";

// Public account-deletion policy page (same page linked from the website footer and
// from the Android / iOS delete dialogs). Surfaced inside the delete dialogs so the
// student can read the policy before confirming.
const DELETE_POLICY_URL = "https://unitedgroovesdance.com/delete-account";

type UpcomingClass = {
  classId?: string;
  className: string;
  startTime?: string;
  endTime?: string;
  dates: string[]; // 'YYYY-MM-DD', today or later
};

// Parse a 'YYYY-MM-DD' string into a LOCAL date (avoids the UTC-midnight off-by-one
// that `new Date("YYYY-MM-DD")` causes in negative-offset timezones).
const formatClassDate = (iso: string): string => {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

const CONFIRM_WORD = "delete";

// Clickable "Account Deletion Policy" text shown inside both delete dialogs.
// Opens the public policy page in a new tab.
const DeletePolicyLink: React.FC = () => (
  <Link
    href={DELETE_POLICY_URL}
    target="_blank"
    rel="noopener noreferrer"
    sx={{ fontSize: "0.875rem", fontWeight: 600 }}
  >
    Account Deletion Policy
  </Link>
);

const DeleteAccountSection: React.FC = () => {
  const apollo = useApolloClient();
  const { logout } = useSession();

  const [breakPrompt, setBreakPrompt] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [upcomingClasses, setUpcomingClasses] = useState(0);
  const [upcomingClassList, setUpcomingClassList] = useState<UpcomingClass[]>([]);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackType, setSnackType] = useState<"success" | "error" | "info">("info");

  const [loadImpact, { loading: loadingImpact }] = useLazyQuery(ACCOUNT_DELETION_IMPACT, {
    fetchPolicy: "network-only",
  });
  const [deleteMyAccount] = useMutation(DELETE_MY_ACCOUNT);

  const notify = (type: "success" | "error" | "info", msg: string) => {
    setSnackType(type);
    setSnackMsg(msg);
    setSnackOpen(true);
  };

  const canConfirm = confirmText.trim().toLowerCase() === CONFIRM_WORD;

  const closeConfirm = () => {
    setConfirmOpen(false);
    setConfirmText("");
  };

  // Tapping "Delete Account" first nudges the student to take a break (sign out and
  // resume later) instead of closing. The impact check only runs if they continue.
  const handleDeleteClick = () => setBreakPrompt(true);

  const handleSignOutInstead = async () => {
    setBreakPrompt(false);
    await apollo.clearStore().catch(() => undefined);
    await logout();
  };

  const proceedToDeletion = async () => {
    setBreakPrompt(false);
    try {
      const res = await loadImpact();
      const data = res.data?.accountDeletionImpact?.data;
      setUpcomingClasses(data?.upcomingClasses ?? 0);
      setUpcomingClassList(data?.upcomingClassList ?? []);
      setConfirmText("");
      setConfirmOpen(true);
    } catch (e: any) {
      notify("error", e?.message || "Could not check your account. Please try again.");
    }
  };

  const handleConfirmDelete = async () => {
    if (!canConfirm) return;
    try {
      setBusy(true);
      const { data } = await deleteMyAccount({ variables: { confirm: true } });
      const result = data?.deleteMyAccount;
      closeConfirm();
      if (!result?.success) {
        notify("error", result?.message || "Failed to close account.");
        return;
      }
      // The account is archived immediately (data retained per policy) — sign out.
      await apollo.clearStore().catch(() => undefined);
      await logout();
    } catch (e: any) {
      notify("error", e?.message || "Failed to close account.");
    } finally {
      setBusy(false);
    }
  };

  const handleContactAdmin = () => {
    closeConfirm();
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=Account%20assistance%20request`;
  };

  return (
    <Box
      sx={{
        mt: 3,
        p: { xs: 2, sm: 2.5 },
        border: "1px solid #FDA29B",
        borderRadius: "12px",
        backgroundColor: "#FFFBFA",
      }}
    >
      <Typography sx={{ fontWeight: 800, color: "#B42318", mb: 0.5 }}>
        Delete Account
      </Typography>
      <Typography sx={{ fontSize: "0.85rem", color: "#667085", mb: 1.5 }}>
        Prefer a break? Just sign out — your account stays active and your progress and
        purchase history are saved. Deleting closes your account and cancels any remaining
        booked classes with no refund; your details are then retained per our
        data-protection policy.
      </Typography>
      <Button
        variant="outlined"
        color="error"
        onClick={handleDeleteClick}
        disabled={loadingImpact || busy}
        sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 700 }}
      >
        {busy || loadingImpact ? "Working…" : "Delete Account"}
      </Button>

      {/* "Take a break" nudge — encourage signing out instead of deleting */}
      <Dialog open={breakPrompt} onClose={() => setBreakPrompt(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800, display: "flex", justifyContent: "space-between" }}>
          Taking a break?
          <IconButton onClick={() => setBreakPrompt(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.secondary" }}>
            Just sign out to take a break — your account stays active and you can resume
            anytime. Deleting closes your account and cancels any remaining booked classes
            with no refund.
          </DialogContentText>
          <Box sx={{ mt: 1.5 }}>
            <DeletePolicyLink />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexDirection: "column", alignItems: "stretch" }}>
          <Button
            variant="contained"
            startIcon={<LogoutIcon />}
            onClick={handleSignOutInstead}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Sign Out Instead
          </Button>
          <Button
            variant="text"
            color="error"
            onClick={proceedToDeletion}
            sx={{ textTransform: "none" }}
          >
            Continue to Delete
          </Button>
          <Button variant="text" onClick={() => setBreakPrompt(false)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Single confirmation — class count, no-refund warning, type-"Delete" to confirm */}
      <Dialog open={confirmOpen} onClose={closeConfirm} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800, display: "flex", justifyContent: "space-between" }}>
          Delete your account?
          <IconButton onClick={closeConfirm} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div" sx={{ color: "text.secondary" }}>
            {upcomingClassList.length > 0 && (
              <Box
                component="ul"
                sx={{
                  listStyle: "none",
                  m: 0,
                  mb: 2,
                  p: 0,
                  borderRadius: "10px",
                  border: "1px solid #EAECF0",
                  overflow: "hidden",
                }}
              >
                {upcomingClassList.map((cls, i) => (
                  <Box
                    component="li"
                    key={cls.classId || `${cls.className}-${i}`}
                    sx={{ p: 1.25, borderTop: i === 0 ? "none" : "1px solid #F2F4F7" }}
                  >
                    <Typography sx={{ fontWeight: 700, color: "#101828", fontSize: "0.9rem" }}>
                      {cls.className}
                      {cls.startTime ? (
                        <Typography component="span" sx={{ fontWeight: 500, color: "#667085" }}>
                          {" "}
                          · {cls.startTime}
                          {cls.endTime ? `–${cls.endTime}` : ""}
                        </Typography>
                      ) : null}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", color: "#475467", mt: 0.25 }}>
                      {cls.dates.map(formatClassDate).join(", ")}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            <p>
              Are you sure? All remaining <strong>{upcomingClasses}</strong> class
              {upcomingClasses === 1 ? "" : "es"} will be cancelled with no refunds.
            </p>
            <p>
              Your account will be closed. Your details are retained per our data-protection
              policy and removed after the retention period; this cannot be undone by you.
            </p>
            <p>
              <DeletePolicyLink />
            </p>
            <p style={{ marginBottom: 8 }}>
              Type <strong>Delete</strong> below to close your account.
            </p>
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Delete"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            inputProps={{ "aria-label": "Type Delete to confirm" }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
          <Button variant="outlined" onClick={closeConfirm} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button variant="outlined" onClick={handleContactAdmin} sx={{ textTransform: "none" }}>
            Contact Admin
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={busy || !canConfirm}
            sx={{ textTransform: "none" }}
          >
            {busy ? "Closing account…" : "Delete Account"}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomSnackbar
        open={snackOpen}
        message={snackMsg}
        type={snackType}
        onClose={() => setSnackOpen(false)}
      />
    </Box>
  );
};

export default DeleteAccountSection;
